import React, { Suspense, useMemo } from "react";
import { BrowserRouter, Route, Switch, Redirect, useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { selectAuthLifecycle } from "redux/selectors/authSelectors";
import { USER_TYPES } from "constants/userTypes";
import NotFoundScreen from "views/error/NotFoundScreen";
import PublicRoute from "./PublicRoute";
import routes, { AdminLayout } from "./routes";
import companySpaceRoutes from "./companySpaceRoutes";
import candidateSpaceRoutes from "./candidateSpaceRoutes";
import { useAnalyticsTracking } from "../hooks/useAnalyticsTracking";
import { SERVER_URL } from "constants/production";

const KLanding = React.lazy(() => import("views/landing/KLanding"));
const CandidateRediscoveryDemo = React.lazy(() => import("views/landing/CandidateRediscoveryDemo"));
const ResumeAnalysisDemo = React.lazy(() => import("views/landing/ResumeAnalysisDemo"));
const CompanySpaceLayout = React.lazy(() =>
  import("layouts/Space/CompanySpaceLayout")
);
const CandidateSpaceLayout = React.lazy(() =>
  import("layouts/Space/BasicLayout")
);

const fullPageLoaderStyle = {
  width: "100vw",
  height: "100vh",
  display: "flex",
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden !important",
};

const ContentAreaLoader = () => (
  <div
    style={{
      display: "flex",
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
      width: "100%",
    }}
    role="status"
    aria-label="Loading"
  >
    <div className="global-spinner global-spinner--sm" />
  </div>
);

/** Full-viewport loader without MUI — keeps @mui off the critical path for Suspense fallbacks */
const FullPageLoader = () => (
  <div style={fullPageLoaderStyle} role="status" aria-label="Loading">
    <div className="global-spinner" />
  </div>
);

const AnalyticsWrapper = ({ children }) => {
  useAnalyticsTracking();
  return <>{children}</>;
};

/**
 * AuthGuard — Route-level protection gate.
 *
 * Uses the AuthLifecycle state machine:
 *   RESTORING      → show FullPageLoader (session check in progress)
 *   LOGGING_OUT    → show FullPageLoader (logout cleanup in progress — prevents /login flash)
 *   UNAUTHENTICATED → redirect to login (confirmed unauthenticated)
 *   AUTHENTICATED  → render children (confirmed authenticated)
 *
 * Public routes bypass this entirely — they render instantly.
 */
const AuthGuard = ({ children, redirectPath, allowedRoles }) => {
  const authLifecycle = useAppSelector(selectAuthLifecycle);
  const location = useLocation();
  const userType = useAppSelector(
    (state) => state?.userReducer?.user?.userType
  );

  React.useEffect(() => {
    if (authLifecycle === 'UNAUTHENTICATED') {
      // Capture the intended destination before redirecting to login,
      // so we can return the user here after successful authentication.
      if (location.pathname !== '/login' && location.pathname !== '/') {
        const fullPath = location.pathname + (location.search || '');
        window.localStorage.setItem('authReturnUrl', fullPath);
      }
    }
  }, [authLifecycle, location.pathname, location.search]);

  switch (authLifecycle) {
    case 'RESTORING':
    case 'LOGGING_OUT':
      return <FullPageLoader />;

    case 'UNAUTHENTICATED':
      return <Redirect to={redirectPath || "/login"} />;

    case 'AUTHENTICATED':
      // Role-based route authorization
      if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(userType)) {
        const fallback = userType === USER_TYPES.CANDIDATE
          ? "/candidate/applied-jobs"
          : "/dashboard/insights";
        return <Redirect to={fallback} />;
      }
      return children;

    default:
      return <FullPageLoader />;
  }
};

const dashboardRoutes = routes.filter(
  (r) => r.path && r.path.startsWith("/dashboard")
);
const nonDashboardRoutes = routes.filter(
  (r) => !r.path || !r.path.startsWith("/dashboard")
);

const Router = () => {
  const authLifecycle = useAppSelector(selectAuthLifecycle);
  const isAuthenticated = authLifecycle === 'AUTHENTICATED';
  const isSessionRestoring = authLifecycle === 'RESTORING';

  return (
    <BrowserRouter>
      <AnalyticsWrapper>
        <Switch>
          {/*
            Route order matters in <Switch>. We render:
            1. Non-dashboard routes with specific paths (auth, website, candidate, scorecard)
               -- but NOT the /:slug catch-all (it must come last)
            2. /company_space/* routes
            3. /candidate_space/* routes
            4. /dashboard/* routes (AdminLayout persists)
            5. The /:slug catch-all (career pages)
            6. 404 fallback
          */}

          {/* 1. Non-dashboard routes (excluding catch-all /:slug) */}
          {nonDashboardRoutes
            .filter((r) => r.path !== "/:slug")
            .map((prop, key) =>
              prop?.publicAuth ? (
                <PublicRoute
                  key={key}
                  path={prop.path}
                  exact={prop?.exact}
                  component={prop.component}
                  layout={prop?.layout}
                />
              ) : (
                <Route
                  exact={prop?.exact}
                  path={prop.path}
                  key={key}
                  render={(props) => (
                    <Suspense fallback={<FullPageLoader />}>
                      {prop?.protected ? (
                        <AuthGuard redirectPath={prop?.redirectPath} allowedRoles={prop?.allowedRoles}>
                          <MemoizedRouteComponentWithLayout
                            layout={prop?.layout}
                            component={prop?.component}
                            {...props}
                          />
                        </AuthGuard>
                      ) : (
                        <MemoizedRouteComponentWithLayout
                          layout={prop?.layout}
                          component={prop?.component}
                          {...props}
                        />
                      )}
                    </Suspense>
                  )}
                />
              )
            )}

          {/* Short URL redirect route for local development */}
          <Route
            exact
            path="/j/:shortCode"
            render={(props) => {
              const { shortCode } = props.match.params;
              window.location.href = `${SERVER_URL}/j/${shortCode}${props.location.search || ''}`;
              return <FullPageLoader />;
            }}
          />

          <Route
            exact
            path="/s/:shortCode"
            render={(props) => {
              const { shortCode } = props.match.params;
              window.location.href = `${SERVER_URL}/s/${shortCode}${props.location.search || ''}`;
              return <FullPageLoader />;
            }}
          />

          {/* Legacy path → canonical demo URL */}
          <Route exact path="/landing/keelzo">
            <Redirect to="/keelzodemo" />
          </Route>

          {/* Standalone landing page — unlisted, no auth */}
          <Route
            exact
            path="/keelzodemo"
            render={() => (
              <Suspense fallback={<FullPageLoader />}>
                <KLanding />
              </Suspense>
            )}
          />

          {/* Standalone Candidate Rediscovery page */}
          <Route
            exact
            path="/keelzodemo/candidaterediscovery"
            render={() => (
              <Suspense fallback={<FullPageLoader />}>
                <CandidateRediscoveryDemo />
              </Suspense>
            )}
          />

          {/* Standalone Resume Analysis page */}
          <Route
            exact
            path="/keelzodemo/resumeanalysis"
            render={() => (
              <Suspense fallback={<FullPageLoader />}>
                <ResumeAnalysisDemo />
              </Suspense>
            )}
          />

          {/* 2. Company space start */}
          <Route
            exact={true}
            path="/company_space/start/:companyId"
            render={(props) => {
              const { companyId } = props.match.params;
              return (
                <Redirect
                  to={`/company_space/company/start?companyId=${companyId}${props.location.search ? `&${props.location.search.substring(1)}` : ""
                    }`}
                />
              );
            }}
          />

          {/* 2b. Company space routes -- layout persists */}
          <Route path="/company_space/:path?">
            <Suspense fallback={<FullPageLoader />}>
              <CompanySpaceLayout>
                <Switch>
                  {companySpaceRoutes.map((prop, key) => (
                    <Route
                      exact={prop?.exact}
                      path={prop.path}
                      key={key}
                      render={(props) => (
                        <Suspense fallback={<ContentAreaLoader />}>
                          <prop.component {...props} />
                        </Suspense>
                      )}
                    />
                  ))}
                </Switch>
              </CompanySpaceLayout>
            </Suspense>
          </Route>

          {/* 3. Candidate space routes -- layout persists */}
          <Route path="/candidate_space/:candidateId?">
            <Suspense fallback={<FullPageLoader />}>
              <CandidateSpaceLayout>
                <Switch>
                  {candidateSpaceRoutes.map((prop, key) => (
                    <Route
                      exact={prop?.exact}
                      path={prop.path}
                      key={key}
                      render={(props) => (
                        <Suspense fallback={<ContentAreaLoader />}>
                          <prop.component {...props} />
                        </Suspense>
                      )}
                    />
                  ))}
                </Switch>
              </CandidateSpaceLayout>
            </Suspense>
          </Route>

          {/* 4. Dashboard routes -- AdminLayout persists across all /dashboard/* pages */}
          <Route path="/dashboard">
            <AuthGuard allowedRoles={[USER_TYPES.EMPLOYER]}>
              <Suspense fallback={<FullPageLoader />}>
                <AdminLayout>
                  <Suspense fallback={<ContentAreaLoader />}>
                    <Switch>
                      {dashboardRoutes.map((prop, key) => (
                        <Route
                          exact={prop?.exact}
                          path={prop.path}
                          key={key}
                          render={(props) => (
                            <Suspense fallback={<ContentAreaLoader />}>
                              <prop.component {...props} />
                            </Suspense>
                          )}
                        />
                      ))}
                    </Switch>
                  </Suspense>
                </AdminLayout>
              </Suspense>
            </AuthGuard>
          </Route>

          {/* 5. Career page catch-all (must be after /dashboard to avoid hijacking) */}
          {nonDashboardRoutes
            .filter((r) => r.path === "/:slug")
            .map((prop, key) => (
              <Route
                exact={prop?.exact}
                path={prop.path}
                key={`catchall-${key}`}
                render={(props) => (
                  <Suspense fallback={<FullPageLoader />}>
                    <MemoizedRouteComponentWithLayout
                      layout={prop?.layout}
                      component={prop?.component}
                      {...props}
                    />
                  </Suspense>
                )}
              />
            ))}

          {/* 6. 404 fallback */}
          <Route path="*" component={NotFoundScreen} />
        </Switch>
      </AnalyticsWrapper>
    </BrowserRouter>
  );
};

const MemoizedRouteComponentWithLayout = ({
  layout: Layout,
  component: Component,
  ...rest
}) => {
  const MemoizedLayout = useMemo(() => Layout, [Layout]);

  if (MemoizedLayout) {
    return (
      <MemoizedLayout>
        <Component {...rest} />
      </MemoizedLayout>
    );
  } else {
    return <Component {...rest} />;
  }
};

export default Router;
